import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CloudArrowUp, HourglassMedium } from "@phosphor-icons/react";

const KIND_FOR = [
  ["criminal background disclosure", null], // handled by its own card
  ["social security", "ss_card"], ["tb step 2", "tb_test"], ["tb test", "tb_test"],
  ["child abuse", "child_abuse_clearance"], ["photo id", "id_card"],
  ["identogo", "identogo_receipt"], ["residency", "residency_proof"], ["drug test", "drug_test"],
];
const kindFor = (item) => {
  const s = item.toLowerCase();
  for (const [needle, kind] of KIND_FOR) if (s.includes(needle)) return kind;
  return "other";
};

export default function DocumentUploadCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const load = () => api.get("/my/documents").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "staff") load(); }, [user]); // eslint-disable-line

  if (!data) return null;
  const docs = data.documents || {};
  const uploadable = (data.outstanding || []).filter((o) => !o.toLowerCase().includes("criminal background disclosure"));
  const pending = Object.entries(docs).filter(([, d]) => d.status === "pending");
  const rejected = Object.entries(docs).filter(([, d]) => d.status === "rejected");
  if (!uploadable.length && !pending.length && !rejected.length) return null;

  const upload = async (kind) => {
    const file = fileRefs.current[kind]?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_date", dates[kind] || "");
      await api.post(`/my/documents/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — the office will review and approve it");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const Row = ({ item }) => {
    const kind = kindFor(item);
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 px-3 py-2.5" data-testid={`my-doc-row-${kind}`}>
        <span className="flex-1 text-xs font-medium text-stone-700">{item}</span>
        <input type="date" value={dates[kind] || ""} onChange={(e) => setDates((d) => ({ ...d, [kind]: e.target.value }))}
          className="h-7 rounded-md border border-stone-300 px-2 text-[11px]" title="Date on the document" data-testid={`my-doc-date-${kind}`} />
        <input type="file" accept=".pdf,image/*" ref={(el) => (fileRefs.current[kind] = el)} className="hidden" data-testid={`my-doc-file-${kind}`} />
        <button type="button" onClick={() => fileRefs.current[kind]?.click()}
          className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-medium text-stone-600 hover:border-primary hover:text-primary" data-testid={`my-doc-choose-${kind}`}>Choose file</button>
        <button type="button" onClick={() => upload(kind)} disabled={busy === kind}
          className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-50" data-testid={`my-doc-upload-${kind}`}>
          <CloudArrowUp size={12} /> {busy === kind ? "Uploading…" : "Upload"}
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="document-upload-card">
      <p className="text-sm font-semibold text-stone-900">Documents we still need</p>
      <p className="mt-0.5 text-[11px] text-stone-400">Upload a clear photo or PDF. The office reviews and approves each one before it counts as on file.</p>
      {uploadable.length > 0 && <div className="mt-3 space-y-2">{uploadable.map((o) => <Row key={o} item={o} />)}</div>}
      {pending.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="my-docs-pending">
          {pending.map(([k, d]) => (
            <p key={k} className="flex items-center gap-1.5 text-xs text-[#8a5d14]">
              <HourglassMedium size={13} /> {d.label || k} — uploaded, waiting for office review
            </p>
          ))}
        </div>
      )}
      {rejected.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="my-docs-rejected">
          {rejected.map(([k, d]) => (
            <p key={k} className="text-xs text-[#C0392B]">{d.label || k} — not accepted{d.review_note ? `: ${d.review_note}` : ""}. Please upload it again.</p>
          ))}
        </div>
      )}
    </div>
  );
}
